import {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    type ReactNode,
} from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useAuth } from './AuthContext';

interface CurrencyTransitionContextType {
    isCurrencyTransitioning: boolean;
}

const CurrencyTransitionContext = createContext<CurrencyTransitionContextType | null>(null);

interface CurrencyTransitionProviderProps {
    children: ReactNode;
}

/**
 * Query keys that are invalidated when display currency changes.
 * These queries return data with converted values that need to be refetched.
 */
const CURRENCY_SENSITIVE_QUERY_PREFIXES = [
    'batch-ledgers',
    'batch-tags',
    'batch-collections',
    'ledger-detail-batch',
    'ledgers',
    'batch-dashboard-analytics',
    'ledger-stats',
];

export function CurrencyTransitionProvider({ children }: CurrencyTransitionProviderProps) {
    const { settings } = useAuth();
    const currentCurrency = settings?.displayCurrency;

    // Track the last confirmed currency (after refetch completed)
    const lastConfirmedCurrencyRef = useRef<string | undefined>(currentCurrency);

    // Track whether we're in a currency transition
    const [pendingCurrency, setPendingCurrency] = useState<string | null>(null);

    // Check if any currency-sensitive queries are currently fetching
    const fetchingCount = useIsFetching({
        predicate: (query) => {
            const queryKey = query.queryKey[0];
            return CURRENCY_SENSITIVE_QUERY_PREFIXES.includes(queryKey as string);
        },
    });
    const isFetching = fetchingCount > 0;

    // Detect when currency changes in settings
    useEffect(() => {
        if (currentCurrency && currentCurrency !== lastConfirmedCurrencyRef.current) {
            setPendingCurrency(currentCurrency);
        }
    }, [currentCurrency]);

    // Clear pending state when fetching completes
    useEffect(() => {
        if (!isFetching && pendingCurrency) {
            lastConfirmedCurrencyRef.current = pendingCurrency;
            setPendingCurrency(null);
        }
    }, [isFetching, pendingCurrency]);

    const isCurrencyTransitioning = pendingCurrency !== null && isFetching;

    return (
        <CurrencyTransitionContext.Provider value={{ isCurrencyTransitioning }}>
            {children}
        </CurrencyTransitionContext.Provider>
    );
}

export function useCurrencyTransition(): CurrencyTransitionContextType {
    const context = useContext(CurrencyTransitionContext);
    if (!context) {
        throw new Error('useCurrencyTransition must be used within a CurrencyTransitionProvider');
    }
    return context;
}
